import { isArray, isEmpty, isEqual, isNil } from 'lodash'
import { deleteFolder, moveItemsToFolder } from 'services/FoldersApiService'
import { fetchImagesFiles } from 'services/ImagesService'
import { saveProjectAs } from 'actions/projectData/saveProjectAs'
import { sdkService } from '~/services/SDKService/SDKService'
import * as API from '~/ServerAPI'
import * as ActionTypes from '~/actions/ActionTypes'
import { checkStatusLongPolling } from '~/actions/project/checkStatusLongPolling'
import {
  FILE_STATUS,
  MEDIA_FILE_FILTERS,
  SOURCE_FILE_TYPES,
  MEDIA_ERROR_TYPE,
  NW_JS_APP_PROFILES,
  PLAYBACK_STATE,
  FILE_TYPE,
  TASK_STATUS
} from '~/enums'
import { MediaUsedOnDeletionError } from '~/errors/MediaUsedOnDeletionError'
import * as Selectors from '~/selectors'
import { fetchAudioFiles } from '~/services/AudioLibraryService'
import { addSourceFileAtSliderPosition } from './addSourceFileAtSliderPosition'
import { setPlaybackClip } from './playback'
import * as EventTypes from '~/services/SDKService/EventTypes'
import { resetImportFilesData, setErrorMessage, updateImportFilesData } from './mainView'
import { BehaviourSettings } from '~/config/constants/editorSettings'
import { selectBehaviourSettings, selectBatchImportFilesData } from '~/selectors/mainView'
import { addSourceFileByFrameTimeCode } from './addSourceFileByFrameTimeCode'
import { selectActiveProjectId, selectActiveProjectName } from '~/selectors/projectData'
import { newProjectId } from '~/constant'
import { NotFoundError } from '~/errors/NotFoundError'
import { saveProject, saveProjectFileIds } from './projectData/saveProject'
import { deleteAssets } from './timeline'
import { isMediaFile } from '~/helpers/assets/fetchFile'
import { selectSelectedClipId } from '~/selectors/playback'
import { filterAssetsByParams } from '~/helpers/assets/filterAssetsByParams'
import { isCancelledReq, isNetworkError } from '~/errors/handleError'

export const CHECK_STATUS_INTERVAL = NW_JS_APP_PROFILES.includes(__APP_PROFILE__) ? 500 : 1000

/**
 * @param {string} type
 * @param {object} [params]
 * @returns {Promise<API.AxiosResponse<API.FilesResourceResponse>>}
 */
function fetchFiles(type, params) {
  switch (type) {
    case SOURCE_FILE_TYPES.PROJECT_MEDIA:
      return API.getMedia(params)
    case SOURCE_FILE_TYPES.MEDIA:
      return API.getMedia(params)
    case SOURCE_FILE_TYPES.AUDIO:
      return fetchAudioFiles(params)
    case SOURCE_FILE_TYPES.TEXT:
      return API.fetchTextFiles(params)
    case SOURCE_FILE_TYPES.TRANSITIONS:
      return API.fetchTransition(params)
    case SOURCE_FILE_TYPES.IMAGES:
      return fetchImagesFiles(params)
    // TODO: filters/design
    default:
      throw new Error(`Unable to fetch unknown files type: ${type}`)
  }
}

// ---

const getDefaultLoadOptions = sourceFilesGroup => ({
  start: 0,
  count: __CFG__.SOURCE_FILES_MANAGEMENT.LOADING_NUMBER,
  ...sourceFilesGroup.params,
})

const fetchSourceFiles = async (params, filesType, dispatch, getState) => {
  const { start, filter } = params
  const response = await fetchFiles(filesType, {
    ...params,
    filter: filter !== MEDIA_FILE_FILTERS.All ? filter : undefined,
  })
  const items = response.data?.entries || []
  if (filesType === SOURCE_FILE_TYPES.MEDIA) {
    items.filter(item => item.status && item.status !== FILE_STATUS.READY
        && item.status !== FILE_STATUS.UPLOAD).forEach(item => setTimeout(
      checkStatus, CHECK_STATUS_INTERVAL, { id: item.id, type: filesType }, dispatch, getState
    ))
    items.filter(item => item.status === FILE_STATUS.RENDER && item.taskId)
      .forEach(item => setTimeout(checkStatusLongPolling, 0, dispatch, item.taskId, item.id))
  }
  // eslint-disable-next-line camelcase
  const totalCount = response.data?.total_count

  await dispatch({
    type: ActionTypes.SOURCE_FILES_LOADED,
    payload: {
      type: filesType,
      start,
      items,
      totalCount,
    },
  })
}

const uploadSourceFiles = async (state, getState, filesType, options, dispatch) => {
  const sourceFilesGroup = Selectors.getSourceFilesGroup(state, filesType)
  const params = { ...getDefaultLoadOptions(sourceFilesGroup), ...options }

  if (isEqual(sourceFilesGroup.params, params) && !options.force) {
    return
  }

  // eslint-disable-next-line no-param-reassign
  delete params.force

  dispatch({
    type: ActionTypes.SOURCE_FILES_REQUESTED,
    payload: { type: filesType, params },
  })

  try {
    await fetchSourceFiles(params, filesType, dispatch, getState)
  } catch (error) {
    console.error(`Failed to fetch source files type ${filesType}\n`, error)
    error.type = filesType
    dispatch({
      type: ActionTypes.SOURCE_FILES_LOADED,
      payload: error,
      error: true,
    })
  }
}

export const loadSourceFiles = (
  filesType,
  options = {}
) => async (dispatch, getState) => {
  const state = getState()
  if (isArray(filesType)) {
    const promises = filesType.map(async type => {
      await uploadSourceFiles(state, getState, type, options, dispatch)
    })
    await Promise.all(promises)
  } else {
    await uploadSourceFiles(state, getState, filesType, options, dispatch)
  }
}

export const requestDeleteFileOrFolder = async cb => {
  try {
    await cb()
  } catch (e) {
    if (e instanceof MediaUsedOnDeletionError) {
      throw e
    } else {
      console.error(e)
    }
  }
}

export const requestDeleteFile = (id, type, force = false, abortController) => async dispatch => {
  let response = null
  try {
    response = await API.deleteMediaFile(id, force, abortController)
    if (isEmpty(response?.data?.projects)) {
      dispatch({
        type: ActionTypes.SOURCE_FILE_DELETED,
        payload: { id, type },
      })
      dispatch(saveProjectFileIds(id, true))
    }
  } catch (e) {
    dispatch(setErrorMessage(e?.message || e || 'Network error'))
  }
  return response?.data || null
}

export const requestDeleteFolder = (id, type, force = false) => async dispatch => {
  await requestDeleteFileOrFolder(async () => {
    await deleteFolder(id, force)
    dispatch({
      type: ActionTypes.SOURCE_FILE_DELETED,
      payload: { id, type },
    })
  })
}

export const requestDeleteMedia = (items, type, force = false) => async dispatch => {
  for (const item of items) {
    if (item.type === 'folder') {
      dispatch(requestDeleteFolder(item.id, type, force))
    } else {
      dispatch(requestDeleteFile(item.id, type, force))
    }
  }
}

export const updateSourceFile = (file, mediaPool) => ({
  type: ActionTypes.SOURCE_FILE_UPDATED,
  payload: { file, mediaPool },
})

export const setSourceFilesParams = (type, params = {}) => ({
  type: ActionTypes.SOURCE_FILES_SET_PARAMS,
  payload: { type, params },
})

export const setSourceFilesParamsNoReload = (type, params = {}) => ({
  type: ActionTypes.SOURCE_FILES_SET_PARAMS_NO_RELOAD,
  payload: { type, params },
})

export const setSourceFilesUiParams = (type, uiParams = {}) => ({
  type: ActionTypes.SOURCE_FILES_SET_UI_PARAMS,
  payload: { type, uiParams },
})

export const updateSourceFileStatus = (data, mediaPool) => ({
  type: ActionTypes.SOURCE_FILE_STATUS_UPDATED,
  payload: { data, mediaPool },
})

const addFileToTimeline = async (id, dispatch) => {
  await dispatch(addSourceFileAtSliderPosition(id))
  sdkService().sendEvent({
    type: EventTypes.MEDIA_IMPORT_COMPLETE,
    payload: { id },
  })
}

export const updatePreviewVideoSize = ({ width, height }) => ({
  type: ActionTypes.CHANGED_PREVIEW_VIDEO_SIZE,
  payload: { width, height },
})

export const moveMediaItemsToFolder = ({ folders, files,
  targetFolder,
  type }) => async dispatch => {
  try {
    if (targetFolder) {
      await moveItemsToFolder(folders, files, targetFolder)
    } else {
      await moveItemsToFolder(folders, files)
    }

    dispatch({ type: ActionTypes.SOURCE_FILES_MOVED,
      payload: { type, ids: [ ...folders, ...files ] } })
  // eslint-disable-next-line no-empty
  } catch (e) {

  }
}

export const toggleMediaLoading = (type, isLoading) => ({
  type: ActionTypes.SOURCE_FILES_REQUESTED,
  payload: { type, isLoading },
})

export const updateFileRefCount = (
  fileId, type, mediaPool, delta = 1, abortController
) => async (dispatch, getState) => {
  const state = getState()
  const activeProjectId = selectActiveProjectId(state)
  const activeProjectName = selectActiveProjectName(state)
  try {
    const isRemoveProjectFile = delta < 0
    const isProjectMediaPool = mediaPool === SOURCE_FILE_TYPES.PROJECT_MEDIA
    if (isProjectMediaPool) {
      if (activeProjectId === newProjectId) {
        await dispatch(saveProjectAs(activeProjectName))
      } else {
        dispatch(saveProjectFileIds(fileId, isRemoveProjectFile))
        await dispatch(saveProject())
      }
    }

    let mediaFileData = {}
    if (isMediaFile(fileId)) {
      await API.updateReference(fileId, delta, abortController)
      const file = await API.getMediaFile(fileId, type, undefined, undefined, abortController)
      mediaFileData = file.data
    }

    if (isRemoveProjectFile) {
      const allAssets = Selectors.getAssets(state)
      dispatch({
        type: ActionTypes.SOURCE_FILE_DELETED,
        payload: { id: fileId, type: mediaPool, mediaPool },
      })
      if (!isEmpty(allAssets)) {
        const deletedAssets = allAssets.filter(a => a.fileId === fileId)
        await dispatch(deleteAssets(deletedAssets))
      }
      if (!isEmpty(mediaFileData)) {
        dispatch({
          type: ActionTypes.UPDATE_MEDIA_FILE_REF_COUNT,
          payload: {
            references: [{ fileId: mediaFileData.id, refCount: mediaFileData.refCount }],
          },
        })
      }
    } else if (isMediaFile(fileId)) {
      await dispatch(updateSourceFile(mediaFileData, mediaPool))
    }
  } catch (e) {
    if (e instanceof NotFoundError) {
      dispatch({
        type: ActionTypes.SOURCE_FILE_DELETED,
        payload: {
          id: fileId,
          type: SOURCE_FILE_TYPES.MEDIA,
          mediaPool: SOURCE_FILE_TYPES.MEDIA,
        },
      })
      const allAssets = Selectors.getAssets(state)
      if (!isEmpty(allAssets)) {
        const deletedAssets = allAssets.filter(a => a.fileId === fileId)
        await dispatch(deleteAssets(deletedAssets))
      }
    } else {
      console.error(`error update count reference file:${fileId} ${e}`)
    }
  }
}

const addFirstImportedFile = mediaPool => async (dispatch, getState) => {
  const state = getState()
  const settings = selectBehaviourSettings(state)
  const allAssets = Selectors.getAssets(state)
  if (isEmpty(allAssets)
    && settings[BehaviourSettings.addFirstImportAtStartTC]) {
    const fileIds = state.projectData.activeProject?.fileIds || []
    const { items, params } = Selectors.getSourceFilesGroup(state, mediaPool)
    const firstImportFile = filterAssetsByParams(items, params)[0]
    if ([ FILE_STATUS.PREPARE_THUMBNAILS, FILE_STATUS.READY ].includes(firstImportFile.status)) {
      // We add to the timeline only the file that is in the project or uploaded to My Media
      if (firstImportFile && ((firstImportFile.refCount >= 1
        && fileIds.includes(firstImportFile.id)) || isNil(firstImportFile.refCount))) {
        await dispatch(addSourceFileByFrameTimeCode([ firstImportFile.id ]))
      }
    }
  }
}

const onReadyToUse = (id, mediaPool, addFirstFile = true) => async (dispatch, getState) => {
  const state = getState()
  const sourceFile = Selectors.getSourceFileById(state, id)
  const behaviourSettings = selectBehaviourSettings(state)
  const playbackState = behaviourSettings?.[BehaviourSettings.autoPlaySelectedMedia]
    ? PLAYBACK_STATE.PLAY : PLAYBACK_STATE.PAUSE

  if (!Selectors.recording.isRecordingCapture(state)) {
    const selectedClipId = selectSelectedClipId(state)
    if (selectedClipId === sourceFile.id) {
      await dispatch(setPlaybackClip(sourceFile, playbackState))
    }
  }
  if (__APP_PROFILE__ === 'package') {
    await addFileToTimeline(sourceFile.id, dispatch)
    return
  }
  if (addFirstFile) {
    await dispatch(addFirstImportedFile(mediaPool))
  }
}

export async function checkStatus({ id, type, mediaPool, abortController }, dispatch, getState) {
  const props = { id, type, mediaPool, abortController }
  if (getState().project.editing && type !== FILE_TYPE.OUTPUT) {
    setTimeout(checkStatus, CHECK_STATUS_INTERVAL, props, dispatch, getState)
    return
  }

  try {
    const response = await API.getMediaFileStatus(id, abortController)
    const { progress, status, error, taskInfo } = response.data

    if (error !== null) {
      dispatch(updateSourceFileStatus({ id, error }, mediaPool))
      return
    }

    const state = getState()
    const sourceFile = Selectors.getSourceFileById(state, id)

    const isReady = [
      FILE_STATUS.PREPARE_THUMBNAILS,
      FILE_STATUS.READY,
    ].includes(status)

    const wasReady = [
      FILE_STATUS.PREPARE_THUMBNAILS,
      FILE_STATUS.READY,
    ].includes(sourceFile.status)

    // First transition into a ready state.
    if (isReady && !wasReady) {
      let data = null

      // Get full media data only when it first becomes usable.
      const response = await API.getMediaFile(id, type, undefined, true, abortController)
      data = response.data

      await dispatch(updateSourceFile(data, mediaPool))
      await dispatch(onReadyToUse(id, mediaPool))
    }

    dispatch(updateSourceFileStatus({
      id,
      progress,
      status,
      taskInfo,
    }, mediaPool))

    if (status === FILE_STATUS.READY) {
      return
    }
    setTimeout(checkStatus, CHECK_STATUS_INTERVAL, props, dispatch, getState)
  } catch (e) {
    if (isCancelledReq(e, abortController)) {
      return
    }

    if (isNetworkError(e)) {
      setTimeout(
        checkStatus,
        CHECK_STATUS_INTERVAL,
        props,
        dispatch,
        getState
      )
    } else {
      const error = {
        message: e.message,
        type: MEDIA_ERROR_TYPE.NETWORK_ERROR,
      }

      dispatch(updateSourceFileStatus({ id, error }, mediaPool))
    }
  }
}


// check status for batch files
export const checkFilesInBatches = (fileList, mediaPool, type) => async (dispatch, getState) => {
  const queue = [ ...fileList ]
  const BATCH_SIZE = 5

  const processQueue = async () => {
    if (queue.length === 0) {
      return
    }
    const currentBatch = queue.splice(0, BATCH_SIZE)

    await Promise.all(currentBatch.map(async id => {
      try {
        const sourceFile = Selectors.getSourceFileById(getState(), id)
        if (!sourceFile) {
          return
        }
        const response = await API.getMediaFileStatus(id)
        const { progress, status, error, taskInfo } = response.data

        if (error) {
          dispatch(updateSourceFileStatus({ id, error }, mediaPool))
          return
        }

        dispatch(updateSourceFileStatus({ id, progress, status, taskInfo }, mediaPool))

        if (status === FILE_STATUS.READY) {
          const { data } = await API.getMediaFile(id, type)
          await dispatch(updateSourceFile(data, mediaPool))
          await dispatch(onReadyToUse(id, mediaPool, false))
        } else {
          queue.push(id)
        }
      } catch (e) {
        console.error(`Polling error for media file ${id}:`, e)
      }
    }))

    if (queue.length > 0) {
      setTimeout(processQueue, CHECK_STATUS_INTERVAL)
    }
  }

  processQueue()
}

const commitMediaFileCreation = (mediaFile, options) => async (dispatch, getState) => {
  const { mediaPool, type, fileId, abortController } = options
  const sourceFile = Selectors.getSourceFileById(getState(), fileId)

  if (!sourceFile) {
    return null
  }
  if (mediaPool === SOURCE_FILE_TYPES.PROJECT_MEDIA) {
    await dispatch(updateFileRefCount(mediaFile.id, type, mediaPool, 1, abortController))
  }
  const { status } = mediaFile

  if ([ FILE_STATUS.READY ].includes(status)) {
    await dispatch(onReadyToUse(fileId, mediaPool, false))
    return null
  }
  return fileId
}


const executeCreateMediaFile = (
  fileId, type, mediaPool = SOURCE_FILE_TYPES.MEDIA, abortController
) => async dispatch => {
  let data = null

  const response = await API.getMediaFile(fileId, type, undefined, false, abortController)
  data = response.data
  await dispatch({
    type: ActionTypes.SOURCE_FILE_CREATED,
    payload: { item: data, mediaPool },
  })
  return dispatch(commitMediaFileCreation(data, { mediaPool, type, fileId, abortController }))
}


export const createMediaFile = (
  fileId, type, mediaPool = SOURCE_FILE_TYPES.MEDIA,
  abortController
) => async (dispatch, getState) => {
  const id = await dispatch(executeCreateMediaFile(fileId, type, mediaPool, abortController))
  const props = { id, type, mediaPool, abortController }
  if (id) {
    setTimeout(
      checkStatus, CHECK_STATUS_INTERVAL, props, dispatch, getState
    )
  }
}

const checkBatchFileUploadStatus = async ({ taskId, mediaPool, type }, dispatch, getState) => {
  let data = null
  const { taskId: currentTaskId } = selectBatchImportFilesData(getState())
  if (!currentTaskId || currentTaskId !== taskId) {
    return
  }

  try {
    const response = await API.getLocalFilesBatchStatus(taskId)
    data = response.data
    const { taskId: currentTaskId } = selectBatchImportFilesData(getState())
    if (!currentTaskId || currentTaskId !== taskId) {
      return
    }
    dispatch(updateImportFilesData(data))
    if (data?.status === TASK_STATUS.COMPLETED) {
      dispatch(toggleMediaLoading(mediaPool))
      dispatch(resetImportFilesData())

      let checkStatusFileList = []
      const { fileIds } = data
      if (fileIds && fileIds.length > 0) {
        for (let i = 0; i < fileIds.length; i += 5) {
          const chunk = fileIds.slice(i, i + 5)
          // eslint-disable-next-line
          let result = await Promise.all(
            chunk.map(fileId => dispatch(
              executeCreateMediaFile(fileId, undefined, mediaPool)
            ))
          )
          result = result.filter(Boolean)
          checkStatusFileList = [ ...checkStatusFileList, ...result ]
        }
      }
      dispatch(toggleMediaLoading(mediaPool, false))
      await dispatch(addFirstImportedFile(mediaPool))
      await dispatch(checkFilesInBatches(checkStatusFileList, mediaPool, type))
      return
    }

    if (data?.status === TASK_STATUS.ABORTED) {
      throw Error('Task was aborted!')
    }
    setTimeout(checkBatchFileUploadStatus,
      CHECK_STATUS_INTERVAL, { taskId, mediaPool, type }, dispatch, getState)
  } catch (e) {
    dispatch(updateImportFilesData({
      error: `Error batchFileUploadTask: ${e?.message ?? e}`,
      progress: 0,
      fileIds: [],
    }))
    console.error(`Error batchFileUploadTask: ${e}`)
  }
}

export const startBatchFileUploadTask = (taskId, mediaPool, type) => async (dispatch, getState) => {
  dispatch(updateImportFilesData({ visible: true, taskId, mediaPool }))
  setTimeout(checkBatchFileUploadStatus,
    CHECK_STATUS_INTERVAL, { taskId, mediaPool, type }, dispatch, getState)
}

export const abortBatchFileUploadTask = taskId => async dispatch => {
  if (!taskId) return
  try {
    await API.deleteLocalFilesBatchRequest(taskId)
    dispatch(resetImportFilesData())
  } catch (e) {
    console.error(`Error abortFileUploadTask: ${e}`)
  }
}
